import React from 'react';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';

import type { IModalDialogContent, ModalDialogOptions, ModalWindow } from '@components/Modal.tsx';
import { ModalButtonType } from '@components/Modal.tsx';
import apiService from '@services/apiService.ts';
import { fileService } from '@services/fileService.ts';
import { toastService } from '@components/Toast.tsx';
import { ImgCheck } from '@components/Images/ImgCheck.tsx';

export type OrphanScanData = {
  userId: string;
  login: string;
  displayName: string;
};

type OrphanFolder = {
  folderName: string;
  sizeBytes: number;
  files: string[];
};

@observer
export class OrphanScanDialog extends React.Component<OrphanScanData> implements IModalDialogContent<void> {
  @observable private _userId: string;
  @observable private _login: string;
  @observable private _displayName: string;
  @observable private _scanning = false;
  @observable private _scanned = false;
  @observable private _orphans: OrphanFolder[] = [];
  @observable private _selected: Set<string> = new Set();
  @observable private _cleaning = false;
  @observable private _cleanResult: { deleted: number } | null = null;
  @observable private _error: string | null = null;

  constructor(props: OrphanScanData) {
    super(props);
    this._userId = props.userId;
    this._login = props.login;
    this._displayName = props.displayName;
    makeObservable(this);
  }

  getModalOptions(window: ModalWindow<void>): ModalDialogOptions<void> {
    return {
      title: `Orphan Scan — ${this._displayName}`,
      width: '580px',
      closeByEscape: true,
      closeOnClickOutside: false,
      buttons: [
        {
          title: 'Close',
          type: ModalButtonType.Cancel,
          onClick: () => window.close(ModalButtonType.Cancel),
        },
      ],
    };
  }

  @action.bound
  private async _onScan() {
    this._scanning = true;
    this._error = null;
    this._scanned = false;
    this._orphans = [];
    this._selected = new Set();
    this._cleanResult = null;
    try {
      const data = await apiService.get<OrphanFolder[]>(`/api/user/${this._userId}/scan-orphans`);
      runInAction(() => {
        this._orphans = data;
        this._scanned = true;
        this._scanning = false;
      });
    } catch {
      const msg = 'Scan failed. Check server logs for details.';
      toastService.error(msg, 'Orphan Scan');
      runInAction(() => {
        this._error = msg;
        this._scanning = false;
      });
    }
  }

  @action.bound
  private _onToggleAll(checked: boolean) {
    this._selected = checked ? new Set(this._orphans.map(o => o.folderName)) : new Set();
  }

  @action.bound
  private _onToggle(folderName: string) {
    const next = new Set(this._selected);
    if (next.has(folderName)) next.delete(folderName);
    else next.add(folderName);
    this._selected = next;
  }

  @action.bound
  private async _onCleanup() {
    if (!this._selected.size) return;
    this._cleaning = true;
    this._error = null;
    try {
      const result = await apiService.post<{ deleted: number }>(`/api/user/${this._userId}/cleanup-orphans`, {
        folderNames: Array.from(this._selected),
      });
      runInAction(() => {
        this._cleanResult = result;
        this._orphans = this._orphans.filter(o => !this._selected.has(o.folderName));
        this._selected = new Set();
        this._cleaning = false;
      });
    } catch {
      const msg = 'Cleanup failed. Check server logs for details.';
      toastService.error(msg, 'Orphan Cleanup');
      runInAction(() => {
        this._error = msg;
        this._cleaning = false;
      });
    }
  }

  render() {
    const totalSelected = Array.from(this._selected)
      .reduce((s, fn) => s + (this._orphans.find(o => o.folderName === fn)?.sizeBytes ?? 0), 0);
    const allChecked = this._orphans.length > 0 && this._selected.size === this._orphans.length;
    const someChecked = this._selected.size > 0 && this._selected.size < this._orphans.length;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
          Scans{' '}
          <code style={{ background: 'var(--surface-alt)', padding: '1px 4px', borderRadius: 3, fontSize: 12 }}>
            upload/{this._login}/
          </code>{' '}
          on disk and finds folders with no matching database record.
        </div>

        {!this._scanned && (
          <button
            className="btn btn-primary"
            onClick={this._onScan}
            disabled={this._scanning}
            style={{ alignSelf: 'flex-start' }}
          >
            {this._scanning ? 'Scanning…' : 'Scan Now'}
          </button>
        )}

        {this._error && (
          <div style={{ color: 'var(--danger)', fontSize: 13 }}>{this._error}</div>
        )}

        {this._cleanResult && (
          <div style={{ color: 'var(--success)', fontSize: 13, background: 'var(--surface-alt)', padding: '8px 12px', borderRadius: 6 }}>
            <ImgCheck /> Cleaned up {this._cleanResult.deleted} folder{this._cleanResult.deleted !== 1 ? 's' : ''}.
            {this._orphans.length > 0 && (
              <>{' '}{this._orphans.length} remaining.</>
            )}
          </div>
        )}

        {this._scanned && this._orphans.length === 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ color: 'var(--success)', fontSize: 13 }}><ImgCheck /> No orphan folders found.</span>
            <button className="btn-link" style={{ fontSize: 13 }} onClick={this._onScan}>Re-scan</button>
          </div>
        )}

        {this._scanned && this._orphans.length > 0 && (
          <>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              Found <b>{this._orphans.length}</b> orphan folder{this._orphans.length !== 1 ? 's' : ''} not tracked in the database.
            </div>

            <div style={{ border: '1px solid var(--border-color)', borderRadius: 6, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--surface-alt)' }}>
                    <th style={{ width: 36, padding: '7px 8px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={allChecked}
                        ref={el => { if (el) el.indeterminate = someChecked; }}
                        onChange={e => this._onToggleAll(e.target.checked)}
                      />
                    </th>
                    <th style={{ textAlign: 'left', padding: '7px 8px', fontWeight: 500, color: 'var(--text-secondary)' }}>Folder ID</th>
                    <th style={{ textAlign: 'left', padding: '7px 8px', fontWeight: 500, color: 'var(--text-secondary)' }}>Contents</th>
                    <th style={{ textAlign: 'right', padding: '7px 8px', fontWeight: 500, color: 'var(--text-secondary)' }}>Size</th>
                  </tr>
                </thead>
                <tbody>
                  {this._orphans.map(o => (
                    <tr
                      key={o.folderName}
                      style={{ borderTop: '1px solid var(--border-color)', cursor: 'pointer' }}
                      onClick={() => this._onToggle(o.folderName)}
                    >
                      <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={this._selected.has(o.folderName)}
                          onChange={() => this._onToggle(o.folderName)}
                          onClick={e => e.stopPropagation()}
                        />
                      </td>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 11, color: 'var(--text-secondary)' }}>
                        {o.folderName}
                      </td>
                      <td style={{ padding: '6px 8px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>
                        {o.files.length === 0
                          ? <span style={{ fontStyle: 'italic' }}>empty</span>
                          : o.files.slice(0, 2).join(', ') + (o.files.length > 2 ? ` +${o.files.length - 2} more` : '')}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {fileService.formatFileSize(o.sizeBytes)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button
                className="btn btn-danger"
                disabled={this._selected.size === 0 || this._cleaning}
                onClick={this._onCleanup}
              >
                {this._cleaning ? 'Deleting…' : `Delete Selected (${this._selected.size})`}
              </button>
              {this._selected.size > 0 && (
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {fileService.formatFileSize(totalSelected)} will be freed
                </span>
              )}
              <button className="btn-link" style={{ marginLeft: 'auto', fontSize: 13 }} onClick={this._onScan}>
                Re-scan
              </button>
            </div>
          </>
        )}
      </div>
    );
  }
}
